import styled, { css } from "styled-components";
import { borderRadius, borderStyle } from "../../../design-system/tokens/borders";
import { rawColors } from "../../../design-system/tokens/colors";
import { Chip } from "../ChartStudioFieldChip/ChartStudioFieldChip.styles";
import { spacing } from "../../../design-system/tokens/spacing";
import { fontSizes, fontWeights, typographyMixin } from "../../../design-system/tokens/typography";
import type { TagChipSize, TagChipTone } from "./TagChip.types";

/**
 * Figma "Status" tag (6433:560619). Same box as the Configure field chip —
 * 4/10 padding, pill radius, Caption 4 high-emphasis text — so it extends that
 * primitive and only swaps the fill and drops the stroke. The accent and outline
 * tones are theme-aware tints of the primary colour.
 */
const statusTagShape = css`
  height: 1.25rem;
  padding: 0 ${spacing[2]};
  border-radius: ${borderRadius.md};
  font-size: ${fontSizes["11"]};
  font-weight: ${fontWeights.bold};
`;

export const Tag = styled(Chip)<{ $tone: TagChipTone; $size: TagChipSize }>`
  justify-content: center;
  max-width: 100%;
  border: ${borderStyle.none};
  background: ${rawColors.structural[100]};

  ${({ $tone }) =>
    $tone === "accent" &&
    css`
      background: ${({ theme }) => theme.colors.primary[100]};
      color: ${({ theme }) => theme.colors.primary[500]};
    `}

  ${({ $tone }) =>
    $tone === "outline" &&
    css`
      background: ${({ theme }) => theme.colors.surface.primary};
      color: ${({ theme }) => theme.colors.primary[500]};
      box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colors.primary[200]};
    `}

  ${({ $tone }) =>
    $tone === "subtle" &&
    css`
      background: ${rawColors.structural[100]};
      color: ${rawColors.text.mediumEmphasis};
      ${typographyMixin("caption", 2)}
    `}

  /* Status-stage tones: 20px, radius 6, 11/700. */
  ${({ $tone }) =>
    $tone === "edited" &&
    css`
      ${statusTagShape}
      background: ${rawColors.accent.tag.info.bg};
      color: ${rawColors.accent.tag.info.fg};
    `}

  ${({ $tone }) =>
    $tone === "restored" &&
    css`
      ${statusTagShape}
      background: ${rawColors.accent.tag.caution.bg};
      color: ${rawColors.accent.tag.caution.fg};
    `}

  ${({ $tone }) =>
    $tone === "draft" &&
    css`
      ${statusTagShape}
      background: ${rawColors.accent.tag.neutral.bg};
      color: ${rawColors.accent.tag.neutral.fg};
    `}

  ${({ $tone }) =>
    $tone === "polished" &&
    css`
      ${statusTagShape}
      background: ${rawColors.accent.tag.accent.bg};
      color: ${rawColors.accent.tag.accent.fg};
    `}

  ${({ $tone }) =>
    $tone === "soon" &&
    css`
      background: ${rawColors.accent.soon.bg};
      color: ${rawColors.accent.soon.text};
    `}

  ${({ $size, $tone }) =>
    $size === "xs" &&
    css`
      height: 1.125rem;
      padding: 0 ${spacing[1.5]};
      border-radius: ${$tone === "soon" ? borderRadius[5] : borderRadius.full};
      ${typographyMixin("overline", 4)}
      font-weight: ${$tone === "soon" ? 800 : 600};
      line-height: 1;
    `}
`;

export const IconSlot = styled.span`
  display: inline-flex;
  flex: none;

  svg {
    width: 0.75rem;
    height: 0.75rem;
  }
`;
